// ================= EXACT SOLIDS (OpenCascade) =================
// The typed parameters stay the source of truth: geo is rebuilt from them as before, and the exact B-rep solid is built
// from geo in a background worker (public/occt-worker.js), so the screen keeps moving while it builds. Until the exact
// solid is ready, solidGeometry() shows the old quick mesh. A new edit stops a running build; a result that doesn't
// belong to the current part is thrown away, so an out-of-date solid is never shown.
// The engine (~15 MB) loads the first time a part needs it, never at page load, and never from file://.
// Anything that can't be built exactly yet keeps the quick mesh: taper threads, square threads, threaded holes and
// bends in Sketch. occtWhy() says why.
var OCST = location.protocol === 'file:' ? 'failed' : 'idle', OCERR = null; // idle | failed
// the worker this app was built with (a hash of its source, stamped by the build; in dev it stays unstamped). A worker
// from another build may not know every op (an older one silently skipped milling cuts), so its solids are never shown.
var OCC_BUILD = '__OCC_WORKER_HASH__';
const occtSameBuild = b => !/^[0-9a-f]{10}$/.test(OCC_BUILD) || b === OCC_BUILD;
var OCC = { key: null, geo: null, res: null, err: null }; // the exact solid for the current part
var OCQ = { key: null, geo: null, timer: 0, pill: 0, pillT: 0, shown: 0, waiting: false };
var OCW = null, OCJ = { seq: 0, cur: 0, busy: false, geo: null, wait: {} }; // worker, jobs
var OCC_PAUSE = 600, OCC_PILL_MS = 500;                   // rebuild after this pause; pill only once a build runs this long

// ---- what can be built exactly. Returns null if fine, or the reason it falls back to the quick mesh.
function occtWhy(g) {
  if (!g) return 'no part';
  if (g.turned) {
    for (const t of g.threads) {
      if (t.t.taper) return 'taper thread';
      if (t.ext && t.p && t.p.d2 !== t.p.d) return 'thread on a taper step';
      if (occThrProf(t.t, t.ext).f < 1e-6) return 'square thread';
    }
    const ext = g.threads.filter(t => t.ext);
    for (let i = 0; i < ext.length; i++) for (let j = i + 1; j < ext.length; j++) if (ext[i].x0 < ext[j].x1 - 1e-9 && ext[j].x0 < ext[i].x1 - 1e-9) return 'overlapping threads';
    return null;
  }
  if (g.block) return null;
  if (g.sketch) {
    if (typeof SKD !== 'undefined' && SKD && SKD.bend && SKD.bend.R > 0) return 'bent part';
    if (g.holes.some(h => h.thr)) return 'threaded hole';
    return null;
  }
  return g.outerPoly ? null : 'unknown part';
}
// thread form, same proportions as thrProfileFn (geometry/threads.js); the worker has the same function
function occThrProf(t, ext) {
  const a = t.form * Math.PI / 360, rmaj = ext ? t.D / 2 : t.Dint / 2, rmin = ext ? t.dMin / 2 : t.D1 / 2, h = rmaj - rmin;
  const f = Math.min(0.45, h * Math.tan(a) / t.P), rest = 1 - 2 * f, c = t.form === 60 ? Math.min(ext ? .125 : .25, rest / 2) : rest / 2;
  return { top: ext ? rmaj : rmin, bot: ext ? rmin : rmaj, f, c, rt: rest - c };
}
// the solid depends on geo, plus the thickness for plates (it isn't part of geo)
function occtKey(g) { return g && !g.turned && !g.sketch && !g.block ? 'plate:' + (thkVal() || 1.5) : 'geo' }

// ---- called by solidGeometry(): the exact mesh if it's ready, otherwise null (and a build gets queued)
// view: every thread helical (the 3D view); otherwise modelled threads only. fine: the STL (call occtPrepare() first).
function occtGeometry(view, fine) {
  if (!geo || OCST === 'failed' || occtWhy(geo)) return null;
  if (OCC.geo === geo && OCC.key === occtKey(geo) && OCC.res) { const M = OCC.res.mesh; return occtToThree(fine && M.stl || (view ? M.view : M.exp)) }
  occtTouch();
  return null;
}
// a part changed: stop any build for an older part, wait for a pause in editing, then build
function occtTouch() {
  if (OCJ.busy && OCJ.geo !== geo) occtCancel();
  if (!geo || OCST === 'failed' || occtWhy(geo)) { occtWait(false); return }
  const key = occtKey(geo);
  if (OCC.geo === geo && OCC.key === key && (OCC.res || OCC.err)) { occtWait(false); return }
  if (OCQ.geo === geo && OCQ.key === key && (OCQ.timer || OCJ.busy)) return;
  clearTimeout(OCQ.timer); OCQ.geo = geo; OCQ.key = key; occtWait(true);
  OCQ.timer = setTimeout(occtRun, OCC_PAUSE);
}
async function occtRun() {
  OCQ.timer = 0;
  const g = geo, key = OCQ.key;
  if (!g || g !== OCQ.geo || key !== occtKey(g)) return;
  occtCancel();
  const job = ++OCJ.cur; OCJ.busy = true; OCJ.geo = g;
  clearTimeout(OCQ.pillT);
  OCQ.pillT = setTimeout(() => { if (OCJ.cur === job && OCJ.busy) { OCQ.shown = job; occtPill('build'); occtDim(true) } }, OCC_PILL_MS);
  const m = await occtAsk(Object.assign({ cmd: 'build' }, occtData(g)));
  if (job !== OCJ.cur || m.stale) return;                  // stopped: a newer edit owns the pill and the wait now
  OCJ.busy = false; clearTimeout(OCQ.pillT);
  const shown = OCQ.shown === job; OCQ.shown = 0;
  if (m.ok && !occtSameBuild(m.build)) {   // a worker from another version: keep the quick model for this session
    m.ok = false; m.fatal = true; m.err = 'the engine is from another version of Benchsmith';
    if (typeof toast === 'function') toast('Benchsmith was updated. Close and reopen it to finish (showing the quick model until then).');
    try { navigator.serviceWorker.getRegistration().then(r => r && r.update()) } catch (e) {}
  }
  if (m.fatal) { OCST = 'failed'; OCERR = m.err }
  if (g !== geo) { occtDim(false); if (shown) occtPill('hide'); return } // edited while it built: never show it
  OCC = { key, geo: g, res: m.ok ? { mesh: { view: m.view, exp: m.exp || m.view }, info: m.info } : null, err: m.ok ? null : m.err, ms: m.ms };
  occtDim(false); if (shown) occtPill(m.ok ? 'ready' : 'hide');
  occtWait(false);
  if (!m.ok && !m.fatal && typeof status === 'function') status('Exact solid failed (' + m.err + '). Showing the quick model.');
  if (m.ok) { if (typeof mode3d !== 'undefined' && mode3d) build3D(); if (typeof ISO !== 'undefined' && ISO.g === g) { ISO.g = null; if (typeof renderCAD === 'function') renderCAD() } }
}
// the STL wants the fine mesh: ask the worker for it (the export buttons wait for this)
async function occtPrepare() {
  if (!OCC.res || OCC.geo !== geo || OCC.res.mesh.stl) return;
  const r = OCC.res, m = await occtAsk({ cmd: 'mesh', which: 'stl' });
  if (m.ok && OCC.res === r) r.mesh.stl = m.mesh;
}
// STEP export: the exact solid as a STEP file (bytes), or why not. plain: threads left as plain cylinders.
function occtStepWhy(g) {
  if (!g) return 'no part';
  if (OCST === 'failed') return location.protocol === 'file:' ? 'open Benchsmith from its web address' : 'the exact-solid engine is not available' + (OCERR ? ' (' + (OCERR.message || OCERR) + ')' : '');
  const w = occtWhy(g); return w ? 'no exact solid for a ' + w : null;
}
async function occtStep(plain) {
  const why = occtStepWhy(geo); if (why) return { ok: false, err: why };
  const g = geo, m = await occtAsk({ cmd: 'step', data: occtData(g), plain: !!plain });
  if (m.stale || g !== geo) return { ok: false, err: 'the part changed while it was writing: tap STEP again' };
  return m;
}
// for the tests: ask the worker about the last solid it built
function occtDebug(what) { return occtAsk({ cmd: 'debug', what }) }

// ---- the worker
function occtSpawn() {
  if (OCW) return OCW;
  try { OCW = new Worker(new URL(/^[0-9a-f]{10}$/.test(OCC_BUILD) ? 'occt-worker-' + OCC_BUILD + '.js' : 'occt-worker.js', document.baseURI), { type: 'module' }) } catch (e) { OCST = 'failed'; OCERR = e; return null }
  OCW.onmessage = e => { const w = OCJ.wait[e.data.id]; if (w) { delete OCJ.wait[e.data.id]; w(e.data) } };
  OCW.onerror = e => { e.preventDefault(); occtStop({ ok: false, fatal: true, err: 'engine failed to load' }) };
  return OCW;
}
function occtAsk(msg) {
  const w = occtSpawn(); if (!w) return Promise.resolve({ ok: false, fatal: true, err: 'no worker' });
  const id = ++OCJ.seq; return new Promise(r => { OCJ.wait[id] = r; w.postMessage(Object.assign({ id }, msg)) });
}
// stop the worker mid-build (the only way to stop OpenCascade); the next job starts a fresh one
function occtCancel() {
  if (!OCJ.busy) return;
  clearTimeout(OCQ.pillT); if (OCQ.shown) { occtPill('hide'); occtDim(false); OCQ.shown = 0 }
  occtStop({ ok: false, stale: true });
}
function occtStop(reply) {
  if (OCW) OCW.terminate(); OCW = null; OCJ.busy = false;
  const w = OCJ.wait; OCJ.wait = {}; Object.values(w).forEach(f => f(reply));
}
// plain data for the worker, taken from geo (the worker has no access to the page)
function occtData(g) {
  if (g.turned) return { kind: 'turned', both: g.threads.some(t => !t.model), g: {
    L: g.L, Rmax: g.Rmax, outer: g.outer, inner: g.inner,
    threads: g.threads.map(t => ({ x0: t.x0, x1: t.x1, ext: t.ext, model: !!t.model, t: { D: t.t.D, Dint: t.t.Dint, dMin: t.t.dMin, D1: t.t.D1, P: t.t.P, form: t.t.form, starts: t.t.starts, lead: t.t.lead, hand: t.t.hand } })),
    keys: g.keys.map(k => ({ a: k.a, b: k.b, w: k.w, t1: k.t1, R: k.R })), flats: g.flats.map(f => ({ a: f.a, b: f.b, h: f.h, two: f.two, R: f.R })), xholes: g.xholes.map(h => ({ c: h.c, d: h.d, R: h.R })) } };
  if (g.block) return { kind: 'block', g: { round: g.kind === 'round', prof: g.prof && !g.prof.plain ? g.prof.pts : null, arcs: g.prof && g.prof.arcs || [], turnBase: g.threads && g.threads.length ? (b => ({ ...b, outerF: undefined, threads: b.threads.map(t => ({ x0: t.x0, x1: t.x1, ext: t.ext, model: true, t: { D: t.t.D, Dint: t.t.Dint, dMin: t.t.dMin, D1: t.t.D1, P: t.t.P, form: t.t.form, starts: t.t.starts, lead: t.t.lead, hand: t.t.hand } })) }))(bkTurnG(g)) : null, L: g.L, W: g.W, T: g.T, holes: g.holes.filter(h => !g.inner || !g.inner.axial.includes(h.i)).map(h => ({ letter: h.letter, P: h.P, D: h.D, r: h.r, depth: h.depth, tipLen: h.tipLen, thru: h.thru })), mills: bkMillCuts(g).map(m => ({ name: m.name, bodies: m.bodies })) } };
  if (g.sketch) return { kind: 'layers', layers: g.layers.map(l => ({ z0: l.z0, z1: l.z1, polys: l.polys.map(p => ({ outer: p.outer, inners: p.inners, holes: p.holes.map(h => ({ stack: h.stack.map(s => ({ z: s.z, r: s.r })) })) })) })) };
  const T = thkVal() || 1.5, op = r => { const q = r.slice(), a = q[0], b = q[q.length - 1]; if (q.length > 2 && Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) q.pop(); return q };
  return { kind: 'layers', layers: [{ z0: 0, z1: T, polys: [{ outer: ccw(op(g.outerPoly)), inners: g.holePolys.map(r => cw(op(r))), holes: [] }] }] };
}

// ---- the UI: pill on the 3D view, part dimmed, Export STL waits
function occtWait(on) {
  OCQ.waiting = on;
  const b = document.getElementById('dl-stl'); if (!b) return;
  if (!b.dataset.html) b.dataset.html = b.innerHTML;
  b.innerHTML = on ? '<b>Export STL</b><span>(waiting for solid)</span>' : b.dataset.html;
  if (typeof gate === 'function') gate();
}
function occtWaiting() { return OCQ.waiting }
function occtPill(state) {
  const p = document.getElementById('occ-pill'); if (!p) return;
  clearTimeout(OCQ.pill); p.classList.remove('ready', 'fade');
  if (state === 'build') { p.textContent = 'Building exact solid… a few seconds'; p.hidden = false; return }
  if (state !== 'ready') { p.hidden = true; return }       // a failed build says why in the status line instead
  p.textContent = 'Exact solid ready'; p.classList.add('ready'); p.hidden = false;
  OCQ.pill = setTimeout(() => { p.classList.add('fade'); OCQ.pill = setTimeout(() => { p.hidden = true; p.classList.remove('fade', 'ready') }, 400) }, 2000);
}
function occtDim(on) {
  if (typeof R3 === 'undefined' || !R3 || !R3.mesh) return;
  [R3.mesh, R3.edges].forEach(o => { if (!o) return; o.material.transparent = on; o.material.opacity = on ? 0.55 : 1; o.material.needsUpdate = true });
  if (typeof place === 'function') place();
}
function occtToThree(m) {
  const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(m.pos, 3)); g.setAttribute('normal', new THREE.BufferAttribute(m.nor, 3));
  g.userData.faceOf = m.faceOf; g.userData.exact = true; return g;
}
